import { create } from "zustand";
import { apiRequest } from "@/lib/api";
import { getAccessToken } from "@/lib/auth";

export interface WorkspaceFile {
  id: string;
  name: string;
  type: "file" | "folder";
  parentId?: string;
  content?: string;
  tags?: string[];
  lastModified?: string;
  project?: string;
  documentId?: string;
}

export type SaveStatus = "idle" | "saving" | "saved" | "error";

interface FilesState {
  files: WorkspaceFile[];
  spaces: any[];
  loading: boolean;
  error: string | null;
  saveStatus: SaveStatus;

  loadWorkspace: () => Promise<void>;
  createFile: (
    name: string,
    parentId?: string,
    isFolder?: boolean,
    project?: string,
    customTempId?: string,
  ) => Promise<string>;
  deleteFile: (fileId: string) => void;
  hydrateFileContent: (fileId: string, content: string) => void;
  updateFileContent: (fileId: string, content: string) => void;
  updateFileTags: (fileId: string, tags: string[]) => void;
  updateFileTitle: (fileId: string, title: string) => void;
  moveFile: (fileId: string, newParentId: string | undefined) => boolean;
  setFiles: (files: WorkspaceFile[]) => void;
  findFileByName: (name: string) => WorkspaceFile | undefined;
}

const getIdsToDelete = (id: string, allFiles: WorkspaceFile[]): string[] => {
  const ids = [id];
  const children = allFiles.filter((f) => f.parentId === id);
  children.forEach((child) => {
    ids.push(...getIdsToDelete(child.id, allFiles));
  });
  return ids;
};

const contentSaveTimers = new Map<string, ReturnType<typeof setTimeout>>();

export const useFilesStore = create<FilesState>((set, get) => ({
  files: [],
  spaces: [],
  loading: false,
  error: null,
  saveStatus: "idle",

  loadWorkspace: async () => {
    set({ loading: true, error: null });
    try {
      const token = getAccessToken();
      if (!token) {
        set({ loading: false });
        return;
      }

      const existingById = new Map(get().files.map((f) => [f.id, f]));
      const serverSpaces = await apiRequest<any[]>("/v1/spaces");
      set({ spaces: serverSpaces });
      const spaceNameMap: Record<string, string> = {};
      serverSpaces.forEach((s) => {
        spaceNameMap[s.id] = s.name;
      });

      const allFiles: WorkspaceFile[] = [];
      for (const space of serverSpaces) {
        const nodes = await apiRequest<any[]>(`/v1/nodes/space/${space.id}`);
        nodes.forEach((node) => {
          const prev = existingById.get(node.id);
          allFiles.push({
            id: node.id,
            name:
              node.type === "page"
                ? node.title.endsWith(".md")
                  ? node.title
                  : `${node.title}.md`
                : node.title,
            type: node.type === "page" ? "file" : "folder",
            parentId: node.parent_id || undefined,
            project: spaceNameMap[node.space_id],
            documentId: node.document_id || undefined,
            content: prev?.content,
            tags: Array.isArray(node.tags) ? node.tags : prev?.tags || [],
            lastModified: node.updated_at
              ? node.updated_at.slice(0, 16).replace("T", " ")
              : undefined,
          });
        });
      }

      set({ files: allFiles, loading: false });
    } catch (err) {
      set({ error: "Error loading workspace", loading: false });
    }
  },

  createFile: async (
    name: string,
    parentId?: string,
    isFolder: boolean = false,
    project: string = "Wiki Arsenal",
    customTempId?: string,
  ) => {
    const state = get();
    const cleanName = isFolder ? name.replace(/\.md$/i, "") : name.endsWith(".md") ? name : `${name}.md`;
    const cleanTitle = cleanName.replace(/\.md$/i, "");
    const tempId = customTempId || `temp-${Date.now()}`;
    const resolvedParentId = parentId && !parentId.startsWith("temp-") ? parentId : undefined;
    const initialContent = isFolder
      ? undefined
      : `## ${cleanTitle}\n\nComienza a escribir sobre ${cleanTitle} aquí.`;

    const newFile: WorkspaceFile = {
      id: tempId,
      name: cleanName,
      type: isFolder ? "folder" : "file",
      parentId: resolvedParentId,
      project,
      lastModified: new Date().toISOString().slice(0, 16).replace("T", " "),
      content: initialContent,
      tags: [],
    };

    set((s) => ({ files: [...s.files, newFile] }));

    try {
      const space = state.spaces.find((s) => s.name === project) || state.spaces[0];
      if (!space) return tempId;

      const baseSlug = cleanTitle.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      const hasSiblingWithSameName = state.files.some(
        (f) =>
          f.id !== tempId &&
          f.parentId === resolvedParentId &&
          f.name.replace(/\.md$/i, "").toLowerCase() === cleanTitle.toLowerCase(),
      );
      const slug = hasSiblingWithSameName
        ? `${baseSlug || "nota"}-${Date.now().toString(36).slice(-4)}`
        : baseSlug || `node-${Date.now()}`;

      const nodePayload = {
        space_id: space.id,
        parent_id: resolvedParentId ?? null,
        type: isFolder ? "folder" : "page",
        title: cleanTitle,
        slug,
      };

      const serverNode = await apiRequest<any>("/v1/nodes", {
        method: "POST",
        body: JSON.stringify(nodePayload),
      });

      let serverDocId: string | undefined = undefined;
      if (!isFolder) {
        const latestTempFile = get().files.find((f) => f.id === tempId);
        const latestContent = latestTempFile?.content ?? initialContent ?? "";
        const latestTitle = (latestTempFile?.name || cleanName).replace(/\.md$/i, "");

        const docPayload = {
          node_id: serverNode.id,
          title: latestTitle,
          plain_text: latestContent,
          content_json: {
            type: "doc",
            content: [{ type: "paragraph", content: [{ type: "text", text: latestContent }] }],
          },
        };
        const serverDoc = await apiRequest<any>("/v1/documents", {
          method: "POST",
          body: JSON.stringify(docPayload),
        });
        serverDocId = serverDoc.id;
      }

      set((s) => ({
        files: s.files.map((f) => {
          if (f.id === tempId) return { ...f, id: serverNode.id, documentId: serverDocId };
          if (parentId && f.parentId === tempId && f.id !== tempId) return { ...f, parentId: serverNode.id };
          return f;
        }),
      }));

      return serverNode.id;
    } catch (err) {
      set((s) => ({ files: s.files.filter((f) => f.id !== tempId) }));
      throw err;
    }
  },

  deleteFile: (fileId: string) => {
    const state = get();
    const idsToDelete = getIdsToDelete(fileId, state.files);
    for (const id of idsToDelete) {
      const timer = contentSaveTimers.get(id);
      if (timer) {
        clearTimeout(timer);
        contentSaveTimers.delete(id);
      }
    }
    set({ files: state.files.filter((f) => !idsToDelete.includes(f.id)) });

    if (!fileId.startsWith("temp-")) {
      apiRequest(`/v1/nodes/${fileId}`, { method: "DELETE" }).catch(() => {});
    }
  },

  hydrateFileContent: (fileId: string, content: string) => {
    set((s) => ({
      files: s.files.map((f) => (f.id === fileId ? { ...f, content } : f)),
    }));
  },

  updateFileContent: (fileId: string, content: string) => {
    const nowFormatted = new Date().toISOString().slice(0, 16).replace("T", " ");
    set((s) => ({
      saveStatus: "saving",
      files: s.files.map((f) =>
        f.id === fileId ? { ...f, content, lastModified: nowFormatted } : f,
      ),
    }));

    const existingTimer = contentSaveTimers.get(fileId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const timer = setTimeout(() => {
      contentSaveTimers.delete(fileId);
      const latestState = get();
      const file = latestState.files.find((f) => f.id === fileId);
      if (!file?.documentId) {
        set({ saveStatus: "idle" });
        return;
      }

      const title = file.name.replace(/\.md$/i, "");
      apiRequest(`/v1/documents/${file.documentId}`, {
        method: "PATCH",
        body: JSON.stringify({
          title,
          plain_text: file.content ?? content,
          content_json: {
            type: "doc",
            content: [{ type: "paragraph", content: [{ type: "text", text: file.content ?? content }] }],
          },
          tags: file.tags,
        }),
      })
        .then(() => {
          set({ saveStatus: "saved" });
        })
        .catch(() => {
          set({ saveStatus: "error" });
        });
    }, 650);

    contentSaveTimers.set(fileId, timer);
  },

  updateFileTags: (fileId: string, tags: string[]) => {
    set((s) => ({
      files: s.files.map((f) => (f.id === fileId ? { ...f, tags } : f)),
    }));

    const state = get();
    const file = state.files.find((f) => f.id === fileId);
    if (file?.documentId) {
      apiRequest(`/v1/documents/${file.documentId}`, {
        method: "PATCH",
        body: JSON.stringify({ tags }),
      }).catch(() => {});
    }
  },

  updateFileTitle: (fileId: string, title: string) => {
    const state = get();
    const existing = state.files.find((f) => f.id === fileId);
    const isFolder = existing?.type === "folder";
    const cleanTitle = title.replace(/\.md$/i, "").trim() || "Sin título";
    const formattedName = isFolder ? cleanTitle : `${cleanTitle}.md`;

    set((s) => ({
      saveStatus: "saving",
      files: s.files.map((f) =>
        f.id === fileId
          ? {
              ...f,
              name: formattedName,
              lastModified: new Date().toISOString().slice(0, 16).replace("T", " "),
            }
          : f,
      ),
    }));

    if (!fileId.startsWith("temp-")) {
      apiRequest(`/v1/nodes/${fileId}`, {
        method: "PATCH",
        body: JSON.stringify({ title: cleanTitle }),
      })
        .then(() => {
          set({ saveStatus: "saved" });
        })
        .catch(() => {
          set({ saveStatus: "error" });
        });
    }
  },

  setFiles: (files: WorkspaceFile[]) => set({ files }),

  moveFile: (fileId: string, newParentId: string | undefined) => {
    const state = get();
    const file = state.files.find((f) => f.id === fileId);
    if (!file) return false;

    // Reject if trying to drop a folder into itself or a descendant
    if (file.id === newParentId) return false;
    if (newParentId) {
      let current: string | undefined = newParentId;
      const visited = new Set<string>();
      while (current) {
        if (current === file.id) return false;
        if (visited.has(current)) break;
        visited.add(current);
        const parent: WorkspaceFile | undefined = state.files.find((f) => f.id === current);
        current = parent?.parentId;
      }
    }

    // Verify destination is a folder if specified
    if (newParentId) {
      const dest = state.files.find((f) => f.id === newParentId);
      if (!dest || dest.type !== "folder") return false;
    }

    set((s) => ({
      files: s.files.map((f) =>
        f.id === fileId ? { ...f, parentId: newParentId } : f,
      ),
    }));

    if (!fileId.startsWith("temp-")) {
      apiRequest(`/v1/nodes/${fileId}`, {
        method: "PATCH",
        body: JSON.stringify({ parent_id: newParentId ?? null }),
      }).catch(() => {});
    }

    return true;
  },

  findFileByName: (name: string) => {
    const cleanSearchName = name.replace(".md", "").toLowerCase().trim();
    return get().files.find(
      (f) =>
        f.type === "file" &&
        (f.name.replace(".md", "").toLowerCase().trim() === cleanSearchName ||
          f.name.toLowerCase().trim() === cleanSearchName),
    );
  },
}));